// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useState } from "react";
import { Stack, TextField } from "@fluentui/react";
import { setTimeout } from 'worker-timers';
import { Send28Filled, Broom28Filled, RecordStop28Filled, DocumentBulletList24Filled, DocumentBulletListOff24Filled } from "@fluentui/react-icons";

import styles from "./QuestionInput.module.css";
import { RetrievalMode } from "../../api"

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    clearChatDisabled?: boolean;
    onClearClick?: () => void;
    onStopClick?: () => void;
    isStreaming: boolean;
    useDarkMode: boolean;
    onRetrievalModeChange: (mode: RetrievalMode) => void;
    retrievalMode: RetrievalMode;
}

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    clearChatDisabled,
    onClearClick,
    onStopClick,
    isStreaming,
    useDarkMode,
    onRetrievalModeChange,
    retrievalMode
}: Props) => {

    const [question, setQuestion] = useState<string>("");
    const [isStopping, setIsStopping] = useState(false);
    const [isRagEnabled, setIsRagEnabled] = useState(true);
    const sendQuestionDisabled = disabled || !question.trim() || isStreaming;
    const [clearChatTextEnabled, setClearChatTextEnable] = useState<boolean>(true);

    const tooltipHtml = isRagEnabled
? `You are using <b>document interaction mode</b>. When you ask a question, responses provided will be based on information sourced from uploaded documentation. Citations will be included.`
: `You are using <b>chat direct</b> mode which provides a similar experience to publicly available generative AI applications. When you ask a question, responses provided will be based on pre-trained information.`

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else {
            setQuestion(newValue);
        }
    };

    const onMouseEnter = () => {
        setClearChatTextEnable(false);
    };

    const onMouseLeave = () => {
        setClearChatTextEnable(true);
    };

    const handleStopClick = async () => {
        if (isStopping) {
            return;
        };

        if (onStopClick) {
            setIsStopping(true);
            try {
                onStopClick();
            } catch (error) { }
            setTimeout(() => {
                setIsStopping(false);
            }, 500); // Change back after 500ms
        }
    };

    const handleRagClick = () => {
        if (retrievalMode === RetrievalMode.None) {
            // If currently "none", switch to "hybrid" upon click
            onRetrievalModeChange(RetrievalMode.Hybrid);
        } else {
            // If currently anything other than "none", switch to "none" upon click
            onRetrievalModeChange(RetrievalMode.None);
        }
    };

    useEffect(() => {
        setIsRagEnabled(retrievalMode !== RetrievalMode.None);
    }, [retrievalMode]);

    return (
        <Stack>
            <Stack.Item>
                <Stack horizontal className={useDarkMode ? styles.questionInputContainerDark : styles.questionInputContainer}>
                    <div className={styles.questionClearButtonsContainer} data-tour="questionInput">
                        <div
                            className={`${styles.questionClearChatButton} ${clearChatDisabled ? styles.disablePointer : ''}`}
                            aria-label="Clear chat button"
                            onClick={clearChatDisabled ? undefined : onClearClick}
                            onMouseEnter={clearChatDisabled ? undefined : onMouseEnter}
                            onMouseLeave={clearChatDisabled ? undefined : onMouseLeave}>
                            <Broom28Filled primaryFill="rgba(255, 255, 255, 1)" />
                            <span hidden={clearChatDisabled || clearChatTextEnabled}>Clear Chat</span>
                        </div>
                    </div>
                    <TextField
                        id="questionInput"
                        className={styles.questionInputTextArea}
                        placeholder={placeholder}
                        multiline
                        resizable={false}
                        borderless
                        value={question}
                        onChange={onQuestionChange}
                        onKeyDown={onEnterPress}
                    />
                    <div className={styles.questionInputButtonsContainer}>
                        <div
                            id="ragEnabled"
                            className={`${styles.questionInputSendButton} ${isStreaming ? styles.questionInputSendButtonDisabled : ""}`}
                            aria-label="Enable/Disable Document Search"
                            data-tooltip-id="ragEnabled-tooltip"
                            data-tooltip-html={tooltipHtml}
                            onClick={handleRagClick}>
                            {isRagEnabled ? (
                                <DocumentBulletList24Filled primaryFill="#28a745" />
                            ) : (
                                <DocumentBulletListOff24Filled primaryFill="#bd7979" />
                            )}
                        </div>
                        {isStreaming ? (
                            <div className={`${styles.questionInputSendButton} ${isStopping ? styles.questionInputSendButtonDisabled : ''}`}
                                onClick={handleStopClick}>
                                <RecordStop28Filled />
                            </div>
                        ) : (
                            <div
                                id="askQuestion"
                                className={`${styles.questionInputSendButton} ${sendQuestionDisabled ? styles.questionInputSendButtonDisabled : ""}`}
                                aria-label="Ask question button"
                                onClick={sendQuestion}>
                                <Send28Filled primaryFill="#0058a6" />
                            </div>
                        )}
                    </div>
                </Stack>
            </Stack.Item>
        </Stack>
    );
};
